/**
 * Application configuration.
 *
 * Everything tunable lives here so the systems stay free of magic numbers.
 *
 * The avatar is deliberately NOT here — it is described by a JSON manifest in
 * `public/avatars/<id>/avatar.json` and loaded at runtime, so changing the
 * guide, its clip names or its blend shapes needs no code change and no
 * rebuild. See `avatar/profile.ts` and `public/avatars/README.md`.
 *
 * The API base URL is likewise resolved at runtime, because the deployed app
 * and local development point at different backends.
 */

declare const __CLIENT_VERSION__: string;

export const CLIENT_VERSION: string = __CLIENT_VERSION__;

export interface AppConfig {
  /** Where the conversation backend lives. Resolved at boot. */
  apiBaseUrl: string;

  placement: {
    /**
     * Consecutive frames the reticle must hold a valid surface hit before a
     * pinch will commit. This is most of the difference between placement
     * feeling solid and feeling twitchy.
     */
    minStableFrames: number;
    /**
     * Seconds to suppress the reticle after the session starts. Tracking is
     * noisy immediately after entry; showing a reticle the user can't yet
     * place against reads as broken.
     */
    scanDelaySeconds: number;
    /** How far the placement ray reaches, in metres. */
    maxDistanceMeters: number;
    /** Reticle position/rotation smoothing per frame (0..1). */
    followLerp: number;
  };

  chat: {
    /** Words per second for the reveal. Drives the mouth, so it sets the pace. */
    revealWordsPerSecond: number;
    /** Hard cap on reply length; a fixed-size bubble has nowhere to overflow. */
    maxReplyChars: number;
    /** Follow-up prompts offered per reply. Three keeps ordinals unambiguous. */
    maxFollowUps: number;
    /** Whole-request timeout for a chat turn, in milliseconds. */
    requestTimeoutMs: number;
    /** Lines kept in the scrollable transcript. */
    transcriptCapacity: number;
  };

  ui: {
    /** Bubble offset from the avatar's origin, in avatar-local metres. */
    bubbleOffset: readonly [number, number, number];
    /** World-space box the bubble is fitted into, in metres. */
    bubbleSize: readonly [number, number];
  };
}

export const CONFIG: AppConfig = {
  apiBaseUrl: '/api',

  placement: {
    minStableFrames: 5,
    scanDelaySeconds: 1.5,
    maxDistanceMeters: 6,
    followLerp: 0.18,
  },

  chat: {
    revealWordsPerSecond: 8,
    maxReplyChars: 600,
    maxFollowUps: 3,
    requestTimeoutMs: 60_000,
    transcriptCapacity: 200,
  },

  ui: {
    bubbleOffset: [0.42, 1.35, 0],
    bubbleSize: [0.62, 0.44],
  },
};

/**
 * Resolve the conversation backend, in priority order:
 *
 *   1. `?api=https://host/api` — a per-open override, useful when testing a
 *      branch backend from the headset without redeploying.
 *   2. `VITE_API_BASE_URL` — baked in at build time.
 *   3. `/api` — same-origin Pages Functions, the deployed default.
 *
 * Only http(s) origins are accepted; anything else falls through to the default
 * rather than failing at the first fetch with a confusing error.
 */
export function resolveApiBaseUrl(search: string = window.location.search): string {
  const override = new URLSearchParams(search).get('api');
  if (override && isUsableHttpUrl(override)) {
    return stripTrailingSlash(override);
  }

  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    return stripTrailingSlash(fromEnv);
  }

  return CONFIG.apiBaseUrl;
}

function isUsableHttpUrl(value: string): boolean {
  try {
    const url = new URL(value, window.location.href);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}
